import { RecurringProduct } from "@/types/recurring";
import { ProductAttribute } from "@/types/attributes";
import OfflineLogger from '@/utils/offlineLogger';
import { trackRecurringCreated } from '@/lib/analyticsEvents';
import { recurringProductsBusinessServiceEnhanced } from '@/services/business/recurringProductsBusinessService.enhanced';
import { convertEnhancedToLegacy, RecurringProductUpdateData } from '@/types/recurring-unified';
import { recurringProductRepositoryEnhanced } from '@/repositories/recurringProduct.repository.enhanced';
import { convertDBToEnhanced } from '@/types/recurring-unified';

export const getRecurringProducts = async (): Promise<RecurringProduct[]> => {
  try {
    const enhanced = await recurringProductsBusinessServiceEnhanced.getAllRecurringProducts();
    return enhanced.map(convertEnhancedToLegacy);
  } catch (error) {
    OfflineLogger.error('getRecurringProducts', error);
    try {
      const offline = await recurringProductRepositoryEnhanced.getAll();
      return offline.map(db => convertEnhancedToLegacy(convertDBToEnhanced(db)));
    } catch (localError) {
      OfflineLogger.error('getRecurringProducts.localFallback', localError);
      return [];
    }
  }
};

export const addRecurringProduct = async (
  productId: string,
  frequencyDays: number,
  recurrenceGroup?: string,
  attributes?: ProductAttribute[],
  next_purchase_date?: string
): Promise<RecurringProduct | null> => {
  try {
    const enhanced = await recurringProductsBusinessServiceEnhanced.createRecurringProduct({
      productId,
      frequencyDays,
      recurrenceGroup,
      attributes,
      nextPurchaseDate: next_purchase_date
    });
    trackRecurringCreated();
    return convertEnhancedToLegacy(enhanced);
  } catch (error) {
    OfflineLogger.error('addRecurringProduct', error);
    return null;
  }
};

export const updateRecurringProduct = async (
  id: string,
  updates: Partial<RecurringProduct>
): Promise<boolean> => {
  try {
    const mappedUpdates: RecurringProductUpdateData = {
      frequency_days: updates.frequency_days,
      recurrence_group: updates.recurrence_group,
      last_purchase_date: updates.last_purchase_date,
      next_purchase_date: updates.next_purchase_date,
      status: updates.active !== undefined ? (updates.active ? 'active' : 'inactive') : undefined,
      attributes: updates.attributes
    };
    const result = await recurringProductsBusinessServiceEnhanced.updateRecurringProduct(id, mappedUpdates);
    return result !== null;
  } catch (error) {
    OfflineLogger.error('updateRecurringProduct', error);
    return false;
  }
};

export const deleteRecurringProduct = async (id: string): Promise<boolean> => {
  try {
    await recurringProductsBusinessServiceEnhanced.deleteRecurringProduct(id);
    return true;
  } catch (error) {
    OfflineLogger.error('deleteRecurringProduct', error);
    return false;
  }
};

export interface UpcomingRecurringProduct extends RecurringProduct {
  /** Next purchase date adjusted for overdue items */
  effective_next_date?: string | null;
}

export const getUpcomingProducts = async (
  daysThreshold: number = 7
): Promise<UpcomingRecurringProduct[]> => {
  try {
    OfflineLogger.info('getUpcomingProducts', `Getting products due within ${daysThreshold} days`);

    const allProducts = await getRecurringProducts();

    const now = new Date();
    const thresholdDate = new Date(now.getTime() + daysThreshold * 24 * 60 * 60 * 1000);

    const withComputedDates = allProducts.map(product => {
      let effective: Date | null = product.next_purchase_date
        ? new Date(product.next_purchase_date)
        : null;

      if (effective && product.frequency_days > 0) {
        while (effective < now) {
          effective = new Date(effective.getTime() + product.frequency_days * 24 * 60 * 60 * 1000);
        }
      }

      return {
        ...product,
        effective_next_date: effective ? effective.toISOString() : null
      } as UpcomingRecurringProduct;
    });

    const upcomingProducts = withComputedDates.filter(product => {
      if (!product.effective_next_date) return false;
      const nextDate = new Date(product.effective_next_date);
      return nextDate <= thresholdDate && nextDate >= now;
    });

    upcomingProducts.sort((a, b) => {
      if (!a.effective_next_date || !b.effective_next_date) return 0;
      return new Date(a.effective_next_date).getTime() - new Date(b.effective_next_date).getTime();
    });

    OfflineLogger.info('getUpcomingProducts', `Found ${upcomingProducts.length} upcoming products`);
    return upcomingProducts;
  } catch (error) {
    OfflineLogger.error('getUpcomingProducts', error);
    return [];
  }
};
